import {matchPath} from "react-router-dom";

import {UnreachableCode} from "@fleet/common/utils/UnreachableCode";

/**
 *! If you add a new route to the enums below, then also add a new entry in pages and
 *! update routeUtils.getPageNameFromRoute
 */
export enum CommonRoute {
    AUTH = "/login",
    FORGOT_PASSWORD = "/forgotPassword",
    RESET_PASSWORD = "/resetPassword/:token",
    HOME = "/",
    INVOICES = "invoices",
    SETTINGS = "settings",
    VERIFICATION = "/verification/:key",
    VERIFICATION_PASSWORD = "/verification/password/:key",
    VERIFICATION_CONFIRMATION = "/verification/confirmation/:key",
    VERIFICATION_2FA = "/verification/2fa/:key",
    VERIFICATION_2FA_CONFIRMATION = "/verification/2fa/confirmation/:key",
    ADD_COMPANY = "/company/add",
    REGISTER_COMPANY = "/company/add/:hash",
    ONBOARD_COMPANY = "/onboarding/:companyId",
    SMS_NOTIFICATION = "/sms/:id",
}

export enum FleetRoute {
    RENTAL_PAYMENTS = "carRentalPayment",
    COMPANY_SETTINGS = "companySettings",
    COMPANY_SETTINGS_BANKING = "companySettings?tab=banking_details",
    COMPANY_SETTINGS_TAX = "companySettings?tab=tax_info",
    DASHBOARD = "dashboard",
    DRIVER = "drivers/:id",
    DRIVERS = "drivers",
    DRIVER_REGISTRATION = "drivers/registration/:id",
    DRIVER_INVITATION = "drivers/invitation/:id",
    DRIVER_REGISTRATIONS = "drivers?tab=registrations",
    EXPIRING_DOCUMENTS = "expiringDocuments",
    LIVE_MAP = "liveMap",
    ORDERS = "orders",
    REPORTS = "reports",
    VEHICLE = "vehicles/:id",
    VEHICLES = "vehicles",
    VEHICLE_APPLICATIONS = "vehicles?tab=applications",
    VEHICLE_APPLICATION = "vehicles/applications/:id",
    CAMPAIGNS = "campaigns",
    CAMPAIGN_DETAILS = "campaigns/:type/:id",
    BALANCE = "balance",
    NOTIFICATIONS = "notifications",
    SUPPORT = "support",
    SHIFTS = "shifts",
    VEHICLE_LISTING_CREATE = "vehicles/vehicle_listings",
    VEHICLE_LISTING_EDIT = "vehicles/vehicle_listings/:id",
    VEHICLE_LISTING_VIEW = "vehicles/vehicle_listings/details/:id",
    VEHICLE_LISTING_APPLICATION = "vehicles/vehicle_listings/applications",
    VEHICLE_LISTING_VIEW_APPLICATION = "vehicles/vehicle_listings/applications/:applicationId",
}

export enum FleetRouteV2 {
    // Operations -> Tracking
    TRACKING = "operations/tracking",
    LIVE_MAP = "operations/tracking/liveMap",
    ORDER_HISTORY = "operations/tracking?tab=order_history",
    ACTIVITY_LOG = "operations/tracking?tab=activity_log",
    // Operations -> Management
    MANAGEMENT = "operations/management",
    SHIFT_PLANNING = "operations/management?tab=shift_planning",
    ORDER_REQUESTS = "operations/management?tab=order_requests",
    INCOMING_ORDERS = "operations/management?tab=incoming_orders",
    EXPIRING_DOCUMENTS = "operations/management/expiringDocuments",
    // Finances -> Reports
    COMPANY_PERFORMANCE = "finances/reports/companyPerformance",
    COMPANY_BALANCE = "finances/reports/companyBalance",
    DRIVER_EARNINGS = "finances/reports/driverEarnings",
    // Finances -> Invoices
    INVOICES = "finances/invoices",
    PAYOUTS = "finances/invoices?tab=payouts",
    BOLT_INVOICES = "finances/invoices?tab=bolt_invoices",
    FLEET_INVOICES = "finances/invoices?tab=fleet_to_rider",
    COMPENSATIONS = "finances/invoices?tab=compensations",
    // Finance -> Bonus campaigns
    CAMPAIGNS = "finances/campaigns",
    ACTIVE_AND_UPCOMING_CAMPAIGNS = "finances/campaigns?tab=active_and_upcoming",
    FINISHED_CAMPAIGNS = "finances/campaigns?tab=finished",
    CAMPAIGN_DETAILS = "finances/campaigns/:type/:id",
    // Supply -> Drivers
    DRIVERS = "supply/drivers",
    DRIVER = "supply/drivers/:id",
    DRIVER_APPLICATION = "supply/drivers/registration/:id",
    DRIVER_INVITATION = "supply/drivers/invitation/:id",
    DRIVER_PERFORMANCE = "supply/drivers?tab=drivers",
    DRIVER_APPLICATIONS = "supply/drivers?tab=registrations",
    PROPOSED_CANDIDATES = "supply/drivers?tab=proposed_candidates",
    // Supply -> Vehicles
    VEHICLES = "supply/vehicles",
    VEHICLE = "supply/vehicles/:id",
    VEHICLE_LISTING_CREATE = "supply/vehicles/vehicle_listings",
    VEHICLE_LISTING_EDIT = "supply/vehicles/vehicle_listings/:id",
    VEHICLE_LISTING_VIEW = "supply/vehicles/vehicle_listings/details/:id",
    VEHICLE_LISTING_APPLICATION = "supply/vehicles/vehicle_listings/applications",
    VEHICLE_LISTING_VIEW_APPLICATION = "supply/vehicles/vehicle_listings/applications/:applicationId",
    VEHICLE_PERFORMANCE = "supply/vehicles?tab=vehicles",
    VEHICLE_APPLICATIONS = "supply/vehicles?tab=applications",
    VEHICLE_APPLICATION = "supply/vehicles/applications/:id",
    RENTAL_LISTINGS = "supply/vehicles?tab=renting_out",
    RENTAL_PAYMENTS = "supply/vehicles?tab=rental_payments",
    // Settings -> Company settings
    COMPANY_SETTINGS = "settings/companySettings",
    COMPANY_CONTACT_DETAILS = "settings/companySettings?tab=company_details",
    COMPANY_BANKING_INFO = "settings/companySettings?tab=banking_details",
    COMPANY_TAX_INFO = "settings/companySettings?tab=tax_info",
    API_CONNECTIONS = "settings/companySettings/apiConnections",
    // Settings -> User preferences
    USER_PREFERENCES = "settings/userPreferences",
    NOTIFICATION_SETTINGS = "settings/userPreferences?tab=notification_settings",
    LANGUAGE_SELECTION = "settings/userPreferences?tab=language_selection",
    NOTIFICATIONS = "notifications",
    SUPPORT = "support",
}

export enum HoldingFleetRouteV2 {
    // Finances -> Invoices
    INVOICES = "finances/invoices",
    // Settings -> User preferences
    USER_PREFERENCES = "settings/userPreferences",
}

export const SmsNotificationRoutePrefix = "sms";
export const HoldingFleetRoutePrefix = "enterprise";
export const ResetPasswordPrefix = "resetPassword";
export const VerificationPrefix = "verification";
export const CompanyRegistrationPrefix = "company/add";
export const VerificationKeyMinLength = 40;
export const CompanyOnboardingRoutePrefix = "onboarding";
export const DeepLinkPrefix = "l/";

export interface RouteData<T> {
    route: T;
    pathPrefix: string;
    params: Array<{param: string; value: unknown}>;
}

export const getRoutedata = <T extends string>(
    prefixRegex: RegExp,
    path: string,
    routes: Record<string, T>,
): RouteData<T> | undefined => {
    const pathPrefix = prefixRegex.exec(path)?.[0] || "";
    const cleanedPath = path.replace(prefixRegex, "");
    const routeData = getPartialRouteData(cleanedPath, routes);
    if (!routeData.route) {
        return undefined;
    }
    return {
        route: routeData.route,
        params: routeData.params ?? [],
        pathPrefix,
    };
};

const getPartialRouteData = <T extends string>(
    path: string,
    routes: Record<string, T>,
): Partial<Omit<RouteData<T>, "pathPrefix">> => {
    for (const route of Object.values(routes)) {
        const match = matchPath({path: `/${route}`}, `/${path}`);
        if (match) {
            return {
                route,
                params: Object.entries(match.params).map(([param, value]) => ({param, value})),
            };
        }
    }
    return {};
};

export function mapOldRouteToNew(
    routeData: RouteData<CommonRoute | FleetRoute>,
    tab: string | null,
): string | undefined {
    let newRoute: string | undefined;
    const {route} = routeData;
    switch (route) {
        case FleetRoute.DASHBOARD:
            newRoute = FleetRouteV2.COMPANY_PERFORMANCE;
            break;
        case FleetRoute.LIVE_MAP:
            newRoute = FleetRouteV2.LIVE_MAP;
            break;
        case FleetRoute.EXPIRING_DOCUMENTS:
            newRoute = FleetRouteV2.EXPIRING_DOCUMENTS;
            break;
        case FleetRoute.DRIVERS:
            if (tab === "registrations") {
                newRoute = FleetRouteV2.DRIVER_APPLICATIONS;
                break;
            }
            newRoute = FleetRouteV2.DRIVER_PERFORMANCE;
            break;
        case FleetRoute.DRIVER:
            newRoute = FleetRouteV2.DRIVER;
            break;
        case FleetRoute.DRIVER_REGISTRATION:
            newRoute = FleetRouteV2.DRIVER_APPLICATION;
            break;
        case FleetRoute.DRIVER_INVITATION:
            newRoute = FleetRouteV2.DRIVER_INVITATION;
            break;
        case FleetRoute.REPORTS:
            newRoute = FleetRouteV2.DRIVER_EARNINGS;
            break;
        case FleetRoute.VEHICLES:
            if (tab === "applications") {
                newRoute = FleetRouteV2.VEHICLE_APPLICATIONS;
                break;
            }
            if (tab === "renting_out") {
                newRoute = FleetRouteV2.RENTAL_LISTINGS;
                break;
            }
            newRoute = FleetRouteV2.VEHICLE_PERFORMANCE;
            break;
        case FleetRoute.VEHICLE:
            newRoute = FleetRouteV2.VEHICLE;
            break;
        case FleetRoute.VEHICLE_APPLICATION:
            newRoute = FleetRouteV2.VEHICLE_APPLICATION;
            break;
        case FleetRoute.RENTAL_PAYMENTS:
            newRoute = FleetRouteV2.RENTAL_PAYMENTS;
            break;
        case FleetRoute.VEHICLE_LISTING_CREATE:
            newRoute = FleetRouteV2.VEHICLE_LISTING_CREATE;
            break;
        case FleetRoute.VEHICLE_LISTING_EDIT:
            newRoute = FleetRouteV2.VEHICLE_LISTING_EDIT;
            break;
        case FleetRoute.VEHICLE_LISTING_VIEW:
            if (tab === "rental_requests") {
                newRoute = `${FleetRouteV2.VEHICLE_LISTING_VIEW}?tab=rental_requests`;
                break;
            }
            newRoute = FleetRouteV2.VEHICLE_LISTING_VIEW;
            break;
        case FleetRoute.VEHICLE_LISTING_VIEW_APPLICATION:
            newRoute = FleetRouteV2.VEHICLE_LISTING_VIEW_APPLICATION;
            break;
        case CommonRoute.INVOICES:
            if (tab === "bolt_invoices") {
                newRoute = FleetRouteV2.BOLT_INVOICES;
                break;
            }
            if (tab === "compensations") {
                newRoute = FleetRouteV2.COMPENSATIONS;
                break;
            }
            if (tab === "payouts") {
                newRoute = FleetRouteV2.PAYOUTS;
                break;
            }
            newRoute = FleetRouteV2.FLEET_INVOICES;
            break;
        case FleetRoute.ORDERS:
            if (tab === "order_requests") {
                newRoute = FleetRouteV2.ORDER_REQUESTS;
                break;
            }
            if (tab === "incoming_orders") {
                newRoute = FleetRouteV2.INCOMING_ORDERS;
                break;
            }
            newRoute = FleetRouteV2.ORDER_HISTORY;
            break;
        case CommonRoute.SETTINGS:
            if (tab === "api") {
                newRoute = FleetRouteV2.API_CONNECTIONS;
                break;
            }
            newRoute = FleetRouteV2.NOTIFICATION_SETTINGS;
            break;
        case FleetRoute.CAMPAIGNS:
            if (tab === "finished") {
                newRoute = FleetRouteV2.FINISHED_CAMPAIGNS;
                break;
            }
            newRoute = FleetRouteV2.ACTIVE_AND_UPCOMING_CAMPAIGNS;
            break;
        case FleetRoute.CAMPAIGN_DETAILS:
            if (tab === "participants") {
                newRoute = `${FleetRouteV2.CAMPAIGN_DETAILS}?tab=participants`;
                break;
            }
            newRoute = FleetRouteV2.CAMPAIGN_DETAILS;
            break;
        case FleetRoute.COMPANY_SETTINGS:
            if (tab === "banking_details") {
                newRoute = FleetRouteV2.COMPANY_BANKING_INFO;
                break;
            }
            if (tab === "tax_info") {
                newRoute = FleetRouteV2.COMPANY_TAX_INFO;
                break;
            }
            newRoute = FleetRouteV2.COMPANY_CONTACT_DETAILS;
            break;
        case FleetRoute.BALANCE:
            newRoute = FleetRouteV2.COMPANY_BALANCE;
            break;
        case FleetRoute.SHIFTS:
            if (tab === "planning") {
                newRoute = FleetRouteV2.SHIFT_PLANNING;
                break;
            }
            newRoute = FleetRouteV2.ACTIVITY_LOG;
            break;
        case FleetRoute.NOTIFICATIONS:
        case FleetRoute.COMPANY_SETTINGS_BANKING:
        case FleetRoute.COMPANY_SETTINGS_TAX:
        case FleetRoute.DRIVER_REGISTRATIONS:
        case FleetRoute.VEHICLE_APPLICATIONS:
        case FleetRoute.SUPPORT:
        case FleetRoute.VEHICLE_LISTING_APPLICATION:
        case CommonRoute.ADD_COMPANY:
        case CommonRoute.AUTH:
        case CommonRoute.FORGOT_PASSWORD:
        case CommonRoute.RESET_PASSWORD:
        case CommonRoute.VERIFICATION:
        case CommonRoute.VERIFICATION_PASSWORD:
        case CommonRoute.VERIFICATION_CONFIRMATION:
        case CommonRoute.VERIFICATION_2FA:
        case CommonRoute.VERIFICATION_2FA_CONFIRMATION:
        case CommonRoute.SMS_NOTIFICATION:
        case CommonRoute.REGISTER_COMPANY:
        case CommonRoute.HOME:
        case CommonRoute.ONBOARD_COMPANY:
            newRoute = undefined;
            break;
        default:
            newRoute = UnreachableCode.never(route, undefined);
    }
    if (!newRoute) {
        return undefined;
    }
    routeData.params.forEach(({param, value}) => {
        newRoute = newRoute!.replace(`:${param}`, (value as string) || "");
    });
    return routeData.pathPrefix + newRoute;
}

export function mapNewRouteToOld(
    routeData: RouteData<FleetRouteV2 | HoldingFleetRouteV2>,
    tab: string | null,
): string | undefined {
    let newRoute: string | undefined;
    const {route} = routeData;
    switch (route) {
        case FleetRouteV2.COMPANY_PERFORMANCE:
            newRoute = FleetRoute.DASHBOARD;
            break;
        case FleetRouteV2.LIVE_MAP:
            newRoute = FleetRoute.LIVE_MAP;
            break;
        case FleetRouteV2.EXPIRING_DOCUMENTS:
            newRoute = FleetRoute.EXPIRING_DOCUMENTS;
            break;
        case FleetRouteV2.DRIVERS:
            if (tab === "registrations" || tab === "proposed_candidates") {
                newRoute = FleetRoute.DRIVER_REGISTRATIONS;
                break;
            }
            newRoute = FleetRoute.DRIVERS;
            break;
        case FleetRouteV2.DRIVER:
            newRoute = FleetRoute.DRIVER;
            break;
        case FleetRouteV2.DRIVER_APPLICATION:
            newRoute = FleetRoute.DRIVER_REGISTRATION;
            break;
        case FleetRouteV2.DRIVER_INVITATION:
            newRoute = FleetRoute.DRIVER_INVITATION;
            break;
        case FleetRouteV2.DRIVER_EARNINGS:
            newRoute = FleetRoute.REPORTS;
            break;
        case FleetRouteV2.VEHICLES:
            if (tab === "applications") {
                newRoute = FleetRoute.VEHICLE_APPLICATIONS;
                break;
            }
            if (tab === "renting_out") {
                newRoute = `${FleetRoute.VEHICLES}?tab=renting_out`;
                break;
            }
            if (tab === "rental_payments") {
                newRoute = FleetRoute.RENTAL_PAYMENTS;
                break;
            }
            newRoute = FleetRoute.VEHICLES;
            break;
        case FleetRouteV2.VEHICLE:
            newRoute = FleetRoute.VEHICLE;
            break;
        case FleetRouteV2.VEHICLE_APPLICATION:
            newRoute = FleetRoute.VEHICLE_APPLICATION;
            break;
        case FleetRouteV2.VEHICLE_LISTING_CREATE:
            newRoute = FleetRoute.VEHICLE_LISTING_CREATE;
            break;
        case FleetRouteV2.VEHICLE_LISTING_EDIT:
            newRoute = FleetRoute.VEHICLE_LISTING_EDIT;
            break;
        case FleetRouteV2.VEHICLE_LISTING_VIEW:
            if (tab === "rental_requests") {
                newRoute = `${FleetRoute.VEHICLE_LISTING_VIEW}?tab=rental_requests`;
                break;
            }
            newRoute = FleetRoute.VEHICLE_LISTING_VIEW;
            break;
        case FleetRouteV2.VEHICLE_LISTING_VIEW_APPLICATION:
            newRoute = FleetRoute.VEHICLE_LISTING_VIEW_APPLICATION;
            break;
        case FleetRouteV2.INVOICES:
            if (tab === "bolt_invoices") {
                newRoute = `${CommonRoute.INVOICES}?tab=bolt_invoices`;
                break;
            }
            if (tab === "compensations") {
                newRoute = `${CommonRoute.INVOICES}?tab=compensations`;
                break;
            }
            if (tab === "payouts") {
                newRoute = `${CommonRoute.INVOICES}?tab=payouts`;
                break;
            }
            newRoute = CommonRoute.INVOICES;
            break;
        case FleetRouteV2.COMPANY_BALANCE:
            newRoute = FleetRoute.BALANCE;
            break;
        case FleetRouteV2.TRACKING:
            if (tab === "order_history") {
                newRoute = FleetRoute.ORDERS;
            }
            if (tab === "activity_log") {
                newRoute = FleetRoute.SHIFTS;
            }
            break;
        case FleetRouteV2.MANAGEMENT:
            if (tab === "shift_planning") {
                newRoute = `${FleetRoute.SHIFTS}?tab=planning`;
            }
            if (tab === "order_requests") {
                newRoute = `${FleetRoute.ORDERS}?tab=order_requests`;
            }
            if (tab === "incoming_orders") {
                newRoute = `${FleetRoute.ORDERS}?tab=incoming_orders`;
            }
            break;
        case FleetRouteV2.CAMPAIGNS:
            if (tab === "finished") {
                newRoute = `${FleetRoute.CAMPAIGNS}?tab=finished`;
                break;
            }
            newRoute = FleetRoute.CAMPAIGNS;
            break;
        case FleetRouteV2.CAMPAIGN_DETAILS:
            if (tab === "participants") {
                newRoute = `${FleetRoute.CAMPAIGN_DETAILS}?tab=participants`;
                break;
            }
            newRoute = FleetRoute.CAMPAIGN_DETAILS;
            break;
        case FleetRouteV2.COMPANY_SETTINGS:
            if (tab === "banking_details") {
                newRoute = FleetRoute.COMPANY_SETTINGS_BANKING;
                break;
            }
            if (tab === "tax_info") {
                newRoute = FleetRoute.COMPANY_SETTINGS_TAX;
                break;
            }
            newRoute = FleetRoute.COMPANY_SETTINGS;
            break;
        case FleetRouteV2.API_CONNECTIONS:
            newRoute = `${CommonRoute.SETTINGS}?tab=api`;
            break;
        case FleetRouteV2.USER_PREFERENCES:
            newRoute = CommonRoute.SETTINGS;
            break;
        case HoldingFleetRouteV2.INVOICES:
            newRoute = CommonRoute.INVOICES;
            break;
        case HoldingFleetRouteV2.USER_PREFERENCES:
            newRoute = CommonRoute.SETTINGS;
            break;
        case FleetRouteV2.NOTIFICATION_SETTINGS:
        case FleetRouteV2.LANGUAGE_SELECTION:
        case FleetRouteV2.NOTIFICATIONS:
        case FleetRouteV2.SUPPORT:
        case FleetRouteV2.VEHICLE_PERFORMANCE:
        case FleetRouteV2.RENTAL_LISTINGS:
        case FleetRouteV2.VEHICLE_APPLICATIONS:
        case FleetRouteV2.VEHICLE_LISTING_APPLICATION:
        case FleetRouteV2.COMPANY_BANKING_INFO:
        case FleetRouteV2.COMPANY_TAX_INFO:
        case FleetRouteV2.COMPANY_CONTACT_DETAILS:
        case FleetRouteV2.ORDER_HISTORY:
        case FleetRouteV2.ORDER_REQUESTS:
        case FleetRouteV2.INCOMING_ORDERS:
        case FleetRouteV2.SHIFT_PLANNING:
        case FleetRouteV2.ACTIVITY_LOG:
        case FleetRouteV2.DRIVER_PERFORMANCE:
        case FleetRouteV2.DRIVER_APPLICATIONS:
        case FleetRouteV2.PROPOSED_CANDIDATES:
        case FleetRouteV2.ACTIVE_AND_UPCOMING_CAMPAIGNS:
        case FleetRouteV2.FINISHED_CAMPAIGNS:
        case FleetRouteV2.BOLT_INVOICES:
        case FleetRouteV2.FLEET_INVOICES:
        case FleetRouteV2.COMPENSATIONS:
        case FleetRouteV2.PAYOUTS:
        case FleetRouteV2.RENTAL_PAYMENTS:
            newRoute = undefined;
            break;
        default:
            newRoute = UnreachableCode.never(route, undefined);
    }

    if (!newRoute) {
        return undefined;
    }
    routeData.params.forEach(({param, value}) => {
        newRoute = newRoute!.replace(`:${param}`, (value as string) || "");
    });
    return routeData.pathPrefix + newRoute;
}
